import { Mark, mergeAttributes } from '@tiptap/core';
import type { StyleParseRule } from '@tiptap/pm/model';

export interface SubscriptExtensionOptions {
    /**
     * HTML attributes to add to the subscript element.
     * @default {}
     * @example { class: 'foo' }
     */
    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        subscript: {
            /**
             * Set a subscript mark
             * @example editor.commands.setSubscript()
             */
            setSubscript: () => ReturnType;
            /**
             * Toggle a subscript mark
             * @example editor.commands.toggleSubscript()
             */
            toggleSubscript: () => ReturnType;
            /**
             * Unset a subscript mark
             * @example editor.commands.unsetSubscript()
             */
            unsetSubscript: () => ReturnType;
        };
    }
}

/**
 * This extension allows you to create subscript text.
 * @see https://www.tiptap.dev/api/marks/subscript
 */
export const WordSubscript = Mark.create<SubscriptExtensionOptions>({
    name: 'subscript',

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    parseHTML() {
        return [
            {
                tag: 'sub',
            },
            {
                style: 'vertical-align',
                getAttrs(value) {
                    // Don’t match this rule if the vertical align isn’t sub.
                    if (value !== 'sub') {
                        return false;
                    }

                    // If it falls through we’ll match, and this mark will be applied.
                    return null;
                },
            } as StyleParseRule,
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['sub', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    addCommands() {
        return {
            setSubscript:
                () =>
                ({ chain }) => {
                    return chain().focus().setMark(this.name).syncParagraphBreakFontWithUpdatedMark(this.name).run();
                },
            toggleSubscript:
                () =>
                ({ chain }) => {
                    return chain().focus().toggleMark(this.name).syncParagraphBreakFontWithUpdatedMark(this.name).run();
                },
            unsetSubscript:
                () =>
                ({ chain }) => {
                    return chain().focus().unsetMark(this.name).syncParagraphBreakFontWithUpdatedMark(this.name).run();
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            'Mod-,': () => this.editor.commands.toggleSubscript(),
        };
    },
});
